import { http } from '@/core/api';
import { executeRequest } from '@/core/either';
import type { ApiResult } from '@/core/either';

import type {
  B2bPaymentMethodWithLimits,
  InswitchQuoteResponse,
} from '../types/inswitch.type';

export const inswitchService = {
  /**
   * Obtiene los métodos de pago B2B con sus bandas de límites (`amountMin`/`amountMax` por moneda).
   * Endpoint compartido por el flujo de cash-in con tarjeta InSwitch y por el flujo de Payment Links
   * para validar que el monto ingresado esté dentro del rango permitido.
   * @param routeId - ID de la ruta de la wallet seleccionada.
   * @returns Promesa Either con la lista de métodos que incluyen `limits`.
   */
  getB2bPaymentMethodLimits(
    routeId: number
  ): Promise<ApiResult<B2bPaymentMethodWithLimits[]>> {
    return executeRequest(() =>
      http.get<B2bPaymentMethodWithLimits[]>('route/b2b/payment-methods', {
        params: {
          routeId,
          customerTypeB2B: true,
        },
      })
    );
  },

  /**
   * Obtiene la cotización InSwitch (costo de servicio y monto a recibir) vía GET `route/b2b/fee-quote`.
   * Endpoint compartido por el flujo de cash-in con tarjeta InSwitch y por el flujo de Payment Links
   * para mostrar el detalle de costos antes de confirmar el cobro.
   * @param params - Parámetros para la cotización (enviados como query params).
   * @param params.partnerName - Nombre del partner (ej. `INSWITCH_PARTNER_NAME`).
   * @param params.amount - Monto en la moneda de origen.
   * @param params.countryCode - Código de país (alpha-2, ej. 'CO', 'CL').
   * @param params.currencyCode - Código de moneda (ej. 'COP', 'CLP').
   * @returns Promesa Either con `feeAmount` (costo) y `finalAmount` (monto a recibir).
   */
  getInswitchFeeQuote(params: {
    partnerName: string;
    amount: number;
    countryCode: string;
    currencyCode: string;
  }): Promise<ApiResult<InswitchQuoteResponse>> {
    return executeRequest(() =>
      http.get<InswitchQuoteResponse>('route/b2b/fee-quote', { params })
    );
  },
};
